import React, { Fragment, useState, useEffect, useCallback } from 'react';
import arrayMutators from 'final-form-arrays';
import { Field, Form, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import {
	Fab,
	Grid,
	Paper,
	TextField as PlainTextField,
	Typography,
	withStyles,
	FormHelperText,
} from '@material-ui/core';

import ViamenteModal from '../ViamenteModal';
import ConfigCheckboxField from './ConfigCheckboxField';
import ConfigTextField from './ConfigTextField';
import TextField from '../shared/TextField';
import addDaysToUTCDate from '../shared/DateUtils';
import Dialog from './../shared/Dialog';
import useMount from '../../hooks/useMount';
import { parseHiddenFields } from '../../config/hiddenFields';
import { debounce } from 'debounce';
import { ImpersonationTable } from './ImpersonationTable';

const styles = () => ({
	paper: {
		width: '500px',
	},
	form: {
		padding: '15px 15px 15px 15px',
	},
	input: {
		width: '400px',
		marginTop: '10px',
		marginBottom: '10px',
	},
	button: {
		float: 'right',
		marginLeft: '10px',
	},
	fabSave: {
		position: 'fixed',
		bottom: '16px',
		right: '16px',
		zIndex: 1000,
	},
	fabBack: {
		position: 'fixed',
		bottom: '16px',
		right: '84px',
		zIndex: 1000,
	},
	hide: {
		display: 'none',
	},
});

const required = (value) => (value || value === 0 || value === '0' ? undefined : 'Required');
const rowsPerPage = 50;
/**
 * * Converts a date to a friendly string for expiration date
 * @param {string} date - Date to convert
 * @returns {string} - Friendly string for expiration date
 * @example - "Expires at 12:08 pm on 11/22/2024"
 */
const toFriendlyExpireString = (date) => {
	const d = new Date(date);
	return `Expires at ${d.toLocaleTimeString('en-US', { hour12: true })} on ${d.toLocaleDateString(
		'en-US',
	)}`;
};

/**
 * Convert a URL to a standard URL that can be navigated to.
 * This function ensures that the URL has a protocol (http or https)
 * Assumes that the URL will use https by default if no protocol is provided.
 * Urls can have sharp edges like:
 * - `app.pestpac.com`
 * - `localhost:3000`
 *
 * @param {string} url - The URL to convert
 * @return {string} - A properly formatted URL
 * @example
 * `normalizeAppUrl('app.pestpac.com')` returns `https://app.pestpac.com`
 * `normalizeAppUrl('http://app.pestpac.com')` returns `http://app.pestpac.com`
 * `normalizeAppUrl('localhost:3000')` returns `https://localhost:3000`
 */
export const normalizeAppUrl = (url) => {
	if (!url) return '';
	if (url.startsWith('http://') || url.startsWith('https://')) {
		return url;
	}
	return `https://${url}`;
};

const TenantDetail = ({
	tenantDetail = {},
	employees = {},
	classes,
	clearTenantDetail,
	history,
	loadTenant,
	loadEmployees,
	match,
	updateTenantDetail,
	requestOtp,
	clearOtp,
	otp,
}) => {
	const companyKey = match.params.id;
	const {
		count: employeeCount,
		skip: employeeSkip,
		take: employeeTake,
		items: employeeList,
	} = employees;

	const [data, setData] = useState(tenantDetail);
	const [open, setOpen] = useState(false);
	const [dialogState, setDialogState] = useState();
	const [hiddenFields, setHiddenFields] = useState([]);
	const [employeePage, setEmployeePage] = useState(0);
	const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
	const [employeeIncludeOnlyMobile, setEmployeeIncludeOnlyMobile] = useState(false);
	const [shouldOpenPestPacImpersonation, setShouldOpenPestPacImpersonation] = useState(false);
	const [useSSO, setUseSSO] = useState(false);

	useMount(() => {
		loadTenant(companyKey);
		return () => {
			clearOtp();
		};
	});

	const getInitialUseSSO = (config = []) =>
		config.find((c) => c.name.toLowerCase() === 'usesso')?.value === true;

	useEffect(() => {
		setData(tenantDetail);
		setUseSSO(getInitialUseSSO(tenantDetail?.config || []));
	}, [tenantDetail]);

	// TODO: Move to hook
	useEffect(() => {
		if (otp) {
			if (shouldOpenPestPacImpersonation) {
				const appUrl = normalizeAppUrl(tenantDetail.appUrl);
				// open new tab
				const newWindow = window.open(
					`${appUrl}/default.asp?Mode=Login&otp=${otp.code}&CompanyKey=${companyKey}`,
					'_blank',
				);
				if (newWindow) {
					newWindow.opener = null;
					newWindow.focus();
					setShouldOpenPestPacImpersonation(false);
				} else {
					setDialogState({
						open: true,
						title: 'Error',
						body: 'Please allow popups for this website',
						buttons: [
							{
								label: 'OK',
								primary: true,
								onClick: () => setDialogState({ open: false }),
							},
						],
					});
				}
			} else {
				setDialogState({
					open: true,
					title: 'Requested One Time Pass Code',
					body: (
						<Grid container direction="column" alignContent="center">
							<Typography variant="body" style={{ textAlign: 'center' }}>
								Access Code: {otp.code}
							</Typography>
							<Typography variant="caption" style={{ textAlign: 'center' }}>
								{toFriendlyExpireString(otp.expirationDate)}
							</Typography>
						</Grid>
					),
					buttons: [
						{
							label: 'OK',
							primary: true,
							onClick: () => {
								setDialogState({ open: false });
								clearOtp();
							},
						},
					],
				});
			}
		}
	}, [otp]);

	const debouncedLoadEmployees = useCallback(
		debounce((term) => {
			loadEmployees(
				companyKey,
				term,
				employeeIncludeOnlyMobile,
				employeePage * rowsPerPage,
				rowsPerPage,
			);
		}, 300),
		[companyKey, employeeIncludeOnlyMobile, employeePage, loadEmployees, rowsPerPage],
	);

	useEffect(() => {
		debouncedLoadEmployees(employeeSearchTerm);
		return () => debouncedLoadEmployees.clear();
	}, [employeeSearchTerm, employeeIncludeOnlyMobile, employeePage, companyKey, rowsPerPage]);

	const updateHiddenFields = (values) => setHiddenFields(parseHiddenFields(values));

	const handleUpdateGracePeriod = (configFields, fieldName, newValue) => {
		if (fieldName.toLowerCase() === 'usesso') {
			const gracePeriodConfig = configFields.find(
				(c) => c.name.toLowerCase() === 'ssograceperiod',
			);

			if (newValue === true && gracePeriodConfig && !gracePeriodConfig.value)
				gracePeriodConfig.value = addDaysToUTCDate(14);

			setUseSSO(newValue);
		}
	};

	const handleWWPrintExportChange = (configFields, fieldName, newValue, form) => {
		if (fieldName.toLowerCase() === 'usewwprintexport') {
			const databillIndex = configFields.findIndex((c) => c.name.toLowerCase() === 'databill');
			if (databillIndex !== -1) {
				form.change(`config[${databillIndex}].value`, !newValue);
			}
		} else if (fieldName.toLowerCase() === 'databill' && newValue) {
			const wwPrintIndex = configFields.findIndex((c) => c.name.toLowerCase() === 'usewwprintexport');
			if (wwPrintIndex !== -1) {
				form.change(`config[${wwPrintIndex}].value`, false);
			}
		}
	};

	const handleCancel = () => {
		clearTenantDetail();
		history.push('/tenants');
	};

	const handleCloseViamente = () => setOpen(false);

	const handleSave = (values) => {
		const oldRouteOp = data.config.find((c) => c.name === 'routeopt');
		const newRouteOp = values.config.find((c) => c.name === 'routeopt');

		if (oldRouteOp.value === true && newRouteOp.value === false) {
			setDialogState({
				open: true,
				title: 'Disabling RouteOp',
				body: 'You are turning off RouteOp.  All constraints will be cleared from the database.  Continue?',
				buttons: [
					{
						label: 'Cancel',
						primary: false,
						onClick: () => setDialogState({ open: false }),
					},
					{
						label: 'OK',
						primary: true,
						onClick: () => {
							setDialogState({ open: false });
							continueSave(values, true);
						},
					},
				],
			});
		} else {
			continueSave(values, false);
		}
	};

	const continueSave = async (values, disableRouteOp) => {
		await updateTenantDetail({ ...values, disableRouteOp });
		clearTenantDetail();
		history.push('/tenants');
	};

	const handleLaunchViamente = async () => {
		setOpen(true);
	};

	const validate = () => {
		return undefined;
	};

	const handleEmployeeFilterChange = (e) => {
		setEmployeeSearchTerm(e.target.value);
		setEmployeePage(0);
	};

	const handleOnlyMobileChange = (e) => {
		setEmployeeIncludeOnlyMobile(e.target.checked);
		setEmployeePage(0);
	};

	if (!data) {
		return null;
	}

	const { isPsub } = data;

	const getFieldDisabled = (configFieldName) => {
		switch (configFieldName) {
			case 'UsePowerBI':
				return !isPsub;
			case 'UseSmartTraps':
				// don't disable if it's enabled already
				const useSmartTrapsConfig = data.config.find(
					(c) => c.name.toLowerCase() === configFieldName.toLowerCase(),
				);
				return (
					!(data.email && data.email.length > 0) &&
					!(useSmartTrapsConfig && useSmartTrapsConfig.value)
				);
			default:
				return false;
		}
	};

	const getFieldDisplay = (configFieldName, displayName) => {
		switch (configFieldName) {
			case 'UsePowerBI':
				return !isPsub
					? `${displayName} - Contact Engineering To Enable This Feature`
					: displayName;
			case 'UseSmartTraps':
				const useSmartTrapsConfig = data.config.find(
					(c) => c.name.toLowerCase() === configFieldName.toLowerCase(),
				);
				return !(data.email && data.email.length > 0) &&
					!(useSmartTrapsConfig && useSmartTrapsConfig.value)
					? `${displayName} - Company Email is Required`
					: displayName;
			default:
				return displayName;
		}
	};

	return (
		<Grid container direction="row" spacing={3} style={{ flexWrap: 'nowrap' }}>
			<Grid item>
				<Form
					onSubmit={handleSave}
					validate={validate}
					mutators={{ ...arrayMutators }}
					initialValues={data}
					render={({ handleSubmit, pristine, invalid }) => (
						<>
							<form onSubmit={handleSubmit} autoComplete="off">
								<Paper className={classes.paper}>
									<Grid container className={classes.form}>
										<Grid item>
											<Typography>
												<b>{data.companyName}</b>
												<br />
												{data.address}
												<br />
												{data.address2}
												{data.address2 && <br />}
												{data.city}, {data.state} {data.zip}
												<br />
												{data.phone}
											</Typography>
											<FormHelperText>Email</FormHelperText>
											<Typography style={{ display: 'inline-block' }}>
												{data.email}
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<PlainTextField
												value={data.companyKey}
												className={classes.input}
												label="Company Key"
												InputProps={{
													readOnly: true,
												}}
											/>
										</Grid>
										<Grid item xs={12}>
											<PlainTextField
												value={data.sqlInstance}
												className={classes.input}
												label="SQL Instance"
												InputProps={{
													readOnly: true,
												}}
											/>
										</Grid>
										<Grid item xs={12}>
											<PlainTextField
												value={data.databaseName}
												className={classes.input}
												label="Database Name"
												InputProps={{
													readOnly: true,
												}}
											/>
										</Grid>
										<Grid item xs={12}>
											<PlainTextField
												value={data.adminWorkwaveAccount}
												className={classes.input}
												label="Admin WW Account Email"
												InputProps={{
													readOnly: true,
												}}
											/>
										</Grid>
										<Grid item xs={12}>
											<Field
												name="adminPassword"
												component={TextField}
												className={classes.input}
												label="Admin Password"
												validate={
													process.env.NODE_ENV === 'production'
														? required
														: undefined
												}
											/>
										</Grid>
										<Grid item xs={12}>
											<Field
												name="expirationDate"
												component={TextField}
												className={classes.input}
												type="date"
												label="Expiration Date"
												InputLabelProps={{
													shrink: true,
												}}
											/>
										</Grid>
										<FieldArray name="config">
											{({ fields }) =>
												fields.map((name, i) => {
													const {
														name: configFieldName,
														fieldType,
														displayName,
													} = data.config[i];

													const isSSOGracePeriodConfig =
														configFieldName.toLowerCase() ===
														'ssograceperiod';

													return (
														<Fragment key={name}>
															<Grid
																item
																xs={12}
																style={
																	hiddenFields.includes(name)
																		? { display: 'none' }
																		: {}
																}
															>
																{fieldType === 'Checkbox' ? (
																	<ConfigCheckboxField
																		name={name}
																		hiddenFields={hiddenFields}
																		configFieldName={
																			configFieldName
																		}
																		displayName={getFieldDisplay(
																			configFieldName,
																			displayName,
																		)}
																		onLaunchViamente={
																			handleLaunchViamente
																		}
																		disabled={getFieldDisabled(
																			configFieldName,
																		)}
																		onClickHandler={(
																			configFields,
																			fieldName,
																			newValue,
																			form,
																		) => {
																			handleUpdateGracePeriod(
																				configFields,
																				fieldName,
																				newValue,
																			);
																			handleWWPrintExportChange(
																				configFields,
																				fieldName,
																				newValue,
																				form,
																			);
																		}}
																	/>
																) : (
																	<ConfigTextField
																		name={name}
																		hiddenFields={hiddenFields}
																		configFieldName={
																			configFieldName
																		}
																		displayName={displayName}
																		fieldType={fieldType}
																		className={classes.input}
																		disabled={
																			isSSOGracePeriodConfig &&
																			!useSSO
																		}
																	/>
																)}
															</Grid>
														</Fragment>
													);
												})
											}
										</FieldArray>
									</Grid>
									<Fab
										onClick={handleCancel}
										className={classes.fabBack}
										color="secondary"
									>
										<ArrowBackIcon />
									</Fab>
									<Fab
										onClick={handleSubmit}
										className={classes.fabSave}
										disabled={pristine || invalid}
										color="primary"
									>
										<SaveIcon />
									</Fab>
								</Paper>
							</form>
							<FormSpy
								subscription={{ values: true }}
								onChange={() => updateHiddenFields}
							/>
						</>
					)}
				/>
			</Grid>
			{open && <ViamenteModal companyKey={companyKey} onClose={handleCloseViamente} />}
			<Dialog state={dialogState} />
			{employeeList && (
				<ImpersonationTable
					companyKey={companyKey}
					classes={classes}
					handleEmployeeFilterChange={handleEmployeeFilterChange}
					handleOnlyMobileChange={handleOnlyMobileChange}
					rowsPerPage={rowsPerPage}
					employeeList={employeeList || []}
					employeeCount={employeeCount}
					employeePage={employeePage}
					setEmployeePage={setEmployeePage}
					requestOtp={requestOtp}
					setShouldOpenPestPacImpersonation={setShouldOpenPestPacImpersonation}
				/>
			)}
		</Grid>
	);
};

export default withStyles(styles)(TenantDetail);
